import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, roleHome } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LogOut, Menu, LayoutDashboard } from "lucide-react";
import BrandMark from "@/components/public/BrandMark";
import SmartLink from "@/components/public/SmartLink";

const DEFAULT_LINKS = [{ label: "Find a laundry", href: "/search" }];

export default function Navbar({ dark }) {
  const { user, logout } = useAuth();
  const { content } = useSite();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const nav = content?.nav || {};
  const links = nav.links?.length ? nav.links : DEFAULT_LINKS;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onDark = dark && !scrolled;
  const linkCls = onDark ? "text-white/80 hover:text-white" : "text-slate-600 hover:text-slate-900";
  const doLogout = () => { logout(); navigate("/"); };

  return (
    <header data-testid="navbar"
      className={`public-site sticky top-0 z-50 w-full transition-[background-color,border-color,box-shadow] duration-300 ${onDark ? "border-b border-white/10 bg-navy/40 backdrop-blur-md" : "border-b border-slate-200/70 bg-white/85 shadow-[0_1px_0_rgba(15,23,42,0.02)] backdrop-blur-xl"}`}>
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-6 px-5 lg:px-8">
        <BrandMark dark={onDark} />

        <nav className="hidden items-center gap-1 lg:flex">
          {links.map((l, i) => (
            <SmartLink key={i} href={l.href} className={`rounded-full px-4 py-2 text-[15px] font-semibold transition-colors ${linkCls}`} data-testid={`nav-link-${i}`}>{l.label}</SmartLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <Link to="/login" className={`hidden rounded-full px-4 py-2 text-[15px] font-semibold transition-colors sm:inline-flex ${linkCls}`} data-testid="nav-login">{nav.login_label || "Sign in"}</Link>
              <SmartLink href={nav.cta_href || "/register"} data-testid="nav-register"
                className="hidden h-11 items-center whitespace-nowrap rounded-full bg-brand px-5 sm:inline-flex text-[15px] font-bold text-white shadow-lg shadow-brand/25 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-brand-hover">
                {nav.cta_label || "Get started"}
              </SmartLink>
            </>
          ) : (
            <>
              <span className={`hidden text-sm xl:block ${onDark ? "text-white/70" : "text-slate-500"}`}>{user.name}</span>
              <Link to={roleHome(user.role)} data-testid="nav-dashboard"
                className={`inline-flex h-10 items-center gap-1.5 rounded-full px-4 text-sm font-bold transition-colors ${onDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-100 text-slate-900 hover:bg-slate-200"}`}>
                <LayoutDashboard className="h-4 w-4" /> {user.role === "customer" ? "My orders" : "Dashboard"}
              </Link>
              <button onClick={doLogout} title="Log out" data-testid="nav-logout"
                className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${onDark ? "text-white/80 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"}`}>
                <LogOut className="h-4 w-4" />
              </button>
            </>
          )}
          <MobileMenu open={open} setOpen={setOpen} links={links} onDark={onDark} user={user} nav={nav} />
        </div>
      </div>
    </header>
  );
}

function MobileMenu({ open, setOpen, links, onDark, user, nav }) {
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className={`flex h-10 w-10 items-center justify-center rounded-full lg:hidden ${onDark ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"}`} data-testid="nav-mobile-menu" aria-label="Menu">
          <Menu className="h-5 w-5" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="public-site w-72">
        <SheetTitle className="font-heading text-lg font-extrabold">Menu</SheetTitle>
        <div className="mt-6 flex flex-col gap-1">
          {links.map((l, i) => (
            <SmartLink key={i} href={l.href} onNavigate={() => setOpen(false)} className="rounded-xl px-3 py-3 text-base font-semibold text-slate-700 hover:bg-slate-100" data-testid={`nav-mobile-link-${i}`}>{l.label}</SmartLink>
          ))}
          {!user && <Link to="/login" onClick={() => setOpen(false)} className="rounded-xl px-3 py-3 text-base font-semibold text-slate-700 hover:bg-slate-100" data-testid="nav-mobile-login">{nav.login_label || "Sign in"}</Link>}
          {!user && (
            <SmartLink href={nav.cta_href || "/register"} onNavigate={() => setOpen(false)} data-testid="nav-mobile-cta"
              className="mt-4 inline-flex h-12 items-center justify-center rounded-full bg-brand text-base font-bold text-white">{nav.cta_label || "Get started"}</SmartLink>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
